/**
 * AdminUsers Page
 * 
 * User management interface for administrators
 * 
 * Features:
 * - View all users with pagination
 * - Search by username or email
 * - Filter by role
 * - Sort by any column
 * - Update user roles
 * - View user details
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UserTable from '../components/UserTable';
import '../assets/styles/AdminUsers.css';

// Use environment variable for API URL, fallback to localhost for local development
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, [pagination.offset, searchTerm, roleFilter]);

  /**
   * Fetch users from backend with current filters
   */
  const fetchUsers = async () => {
    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('adminToken');
      
      // Build query parameters
      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset,
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (roleFilter) {
        params.append('role', roleFilter);
      }

      const response = await fetch(
        `${API_URL}/admin/users?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setPagination(prev => ({
          ...prev,
          total: data.total
        }));
      } else {
        setError('Failed to load users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Unable to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle user role update
   */
  const handleRoleUpdate = async (userId, newRole) => {
    setIsUpdating(true);

    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(
        `${API_URL}/admin/users/${userId}/role`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ newRole })
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Update user in local state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId 
              ? { ...user, role: newRole }
              : user
          )
        );

        alert(`User role updated successfully to ${newRole}`);
      } else {
        const data = await response.json();
        alert(`Error: ${data.error || 'Failed to update role'}`);
      }
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Unable to update role. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Handle search with debouncing
   */
  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  /**
   * Handle role filter change
   */
  const handleRoleFilter = (value) => {
    setRoleFilter(value);
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  /**
   * Handle pagination - next page
   */
  const handleNextPage = () => {
    if (pagination.offset + pagination.limit < pagination.total) {
      setPagination(prev => ({
        ...prev,
        offset: prev.offset + prev.limit
      }));
    }
  };

  /**
   * Handle pagination - previous page
   */
  const handlePrevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({
        ...prev,
        offset: Math.max(0, prev.offset - prev.limit)
      }));
    }
  };

  /**
   * Calculate current page info
   */
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const startUser = pagination.offset + 1;
  const endUser = Math.min(pagination.offset + pagination.limit, pagination.total);

  return (
    <div className="admin-users-container">
      {/* Header */}
      <header className="admin-users-header">
        <div>
          <h1>User Management</h1>
          <p>View and manage all user accounts</p>
        </div>
        <button 
          className="back-btn" 
          onClick={() => navigate('/admin/dashboard')}
        >
          ← Back to Dashboard
        </button>
      </header>

      {/* Filters and Search */}
      <div className="admin-users-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by username or email..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-box">
          <label>Filter by role:</label>
          <select 
            value={roleFilter} 
            onChange={(e) => handleRoleFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Roles</option>
            <option value="user">Users Only</option>
            <option value="admin">Admins Only</option>
          </select>
        </div>

        <div className="results-count">
          Showing {users.length > 0 ? startUser : 0} - {endUser} of {pagination.total} users
        </div>
      </div>

      {/* Users Table */}
      {isLoading ? (
        <div className="loading-state">
          <p>Loading users...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchUsers}>Retry</button>
        </div>
      ) : (
        <UserTable 
          users={users}
          onRoleUpdate={handleRoleUpdate}
          isUpdating={isUpdating}
        />
      )}

      {/* Pagination */}
      {!isLoading && users.length > 0 && (
        <div className="pagination-controls">
          <button 
            onClick={handlePrevPage}
            disabled={pagination.offset === 0}
            className="pagination-btn"
          >
            ← Previous
          </button>
          
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button 
            onClick={handleNextPage}
            disabled={pagination.offset + pagination.limit >= pagination.total}
            className="pagination-btn"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;

